import React, { useState, useEffect } from 'react';
import { X, Search, Package, ChevronLeft, Plus, Minus, Tag, Calculator } from 'lucide-react';
import { productsApi } from '../services/api';

function SelectProductModal({ isOpen, onClose, onAdd }) {
  const [step, setStep] = useState(1); // 1: Select Product, 2: Enter Details
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState({
    quantity: 1,
    discount: 0,
    batchNumber: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await productsApi.getAllProducts();
      setProducts(data);
      const uniqueCategories = [...new Set(data.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.productName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const isActive = product.isActive !== false;
    return matchesSearch && matchesCategory && isActive;
  });

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setStep(2);
  };

  const handleDetailsChange = (field, value) => {
    setProductDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateAmount = () => {
    if (!selectedProduct) return 0;
    const quantity = Number(productDetails.quantity) || 0;
    const price = Number(selectedProduct.finalPrice || selectedProduct.price) || 0;
    const discount = Number(productDetails.discount) || 0;
    return (quantity * price) - ((quantity * price * discount) / 100);
  };

  const handleContinue = () => {
    if (step === 2 && selectedProduct) {
      const productWithDetails = {
        ...selectedProduct,
        quantity: productDetails.quantity,
        discount: productDetails.discount,
        batchNumber: productDetails.batchNumber,
        amount: calculateAmount()
      };
      onAdd(productWithDetails);

      // Reset to step 1 to select more products instead of fully closing the modal
      setStep(1);
      setSelectedProduct(null);
      setProductDetails({
        quantity: 1,
        discount: 0,
        batchNumber: ''
      });
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedProduct(null);
    setSearchTerm('');
    setSelectedCategory('');
    setProductDetails({
      quantity: 1,
      discount: 0,
      batchNumber: ''
    });
    onClose();
  };

  const handleBack = () => {
    setStep(1);
    setSelectedProduct(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] transition-all">
      <div className="bg-white rounded-[2rem] w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col shadow-2xl border border-white/20">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-[#E1F2ED] bg-[#F4FBF9]">
          <div className="flex items-center space-x-3">
            {step === 2 && (
              <button
                onClick={handleBack}
                className="mr-2 p-2 hover:bg-[#E1F2ED] rounded-full transition-all text-[#4B9C86]"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <Package className="w-6 h-6 text-[#4B9C86]" />
            <h2 className="text-[20px] font-black text-[#1E4D43] uppercase tracking-wide">
              {step === 1 ? 'Select Product' : 'Item Details'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-[#E1F2ED] rounded-full transition-all text-[#4B9C86]"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {step === 1 ? (
          <>
            {/* Search & Filters */}
            <div className="p-8 pb-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4B9C86]" />
                  <input
                    type="text"
                    placeholder="Search product..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-xl focus:outline-none focus:border-[#4B9C86] text-[#1E4D43] font-bold text-sm"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-xl focus:outline-none focus:border-[#4B9C86] text-[#1E4D43] font-bold text-sm appearance-none"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Product List */}
            <div className="flex-1 overflow-y-auto px-8 py-4 space-y-3 custom-scrollbar">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-10 h-10 border-4 border-[#4B9C86] border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-[#1E4D43] font-bold">Loading products...</p>
                </div>
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    className="flex items-center space-x-4 p-4 bg-white border-2 border-[#E1F2ED] rounded-2xl cursor-pointer transition-all hover:border-[#4B9C86] hover:bg-[#F9FBFA] group shadow-sm hover:shadow-md"
                  >
                    <div className="w-14 h-14 bg-[#F2FBF9] rounded-xl flex items-center justify-center border border-[#E1F2ED] p-2 flex-shrink-0">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <Package size={24} className="text-[#4B9C86]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[16px] font-black text-[#1E4D43] truncate">{product.productName}</h3>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-[11px] font-black text-[#4B9C86] uppercase tracking-wider">{product.category}</span>
                        {product.weight && <span className="text-[11px] font-bold text-gray-400">| {product.weight}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[16px] font-black text-[#1E4D43]">₹ {product.finalPrice?.toLocaleString()}</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Stock: {product.stockQuantity || 0}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center opacity-60">
                  <Search size={40} className="text-[#4B9C86] mb-4" />
                  <p className="text-[16px] font-bold text-[#1E4D43]">No matching products found</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="p-8 flex-1 overflow-y-auto space-y-8 custom-scrollbar">
            {/* Selected Item Card */}
            <div className="bg-[#F2FBF9] border-2 border-[#4B9C86] rounded-2xl p-6 flex items-center space-x-6">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center border-2 border-[#BDDBCE] p-3 shadow-inner">
                {selectedProduct.imageUrl ? (
                  <img src={selectedProduct.imageUrl} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Package size={32} className="text-[#4B9C86]" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-[20px] font-black text-[#1E4D43] leading-tight">{selectedProduct.productName}</h3>
                <p className="text-[#4B9C86] font-bold text-sm mt-1">MRP: ₹ {selectedProduct.finalPrice?.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <span className="bg-white px-4 py-2 rounded-xl text-[18px] font-black text-[#4B9C86] shadow-sm border border-[#BDDBCE]">
                  ₹ {calculateAmount().toLocaleString()}
                </span>
                <p className="text-[10px] font-bold text-gray-400 uppercase mt-2">Total Estimated</p>
              </div>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[12px] font-black text-gray-400 uppercase tracking-wider">Purchase Quantity</label>
                <div className="flex items-center space-x-4 bg-[#F2FBF9] rounded-2xl p-2 border-2 border-[#E1F2ED]">
                  <button
                    onClick={() => handleDetailsChange('quantity', Math.max(1, Number(productDetails.quantity) - 1))}
                    className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-[#4B9C86] shadow-sm hover:shadow-md active:scale-95 transition-all"
                  >
                    <Minus size={18} />
                  </button>
                  <input
                    type="number"
                    value={productDetails.quantity}
                    onChange={(e) => handleDetailsChange('quantity', e.target.value)}
                    className="flex-1 bg-transparent border-none text-center font-black text-[20px] text-[#1E4D43] focus:outline-none"
                  />
                  <button
                    onClick={() => handleDetailsChange('quantity', Number(productDetails.quantity) + 1)}
                    className="w-10 h-10 bg-[#4B9C86] rounded-xl flex items-center justify-center text-white shadow-md hover:bg-[#3D826F] active:scale-95 transition-all"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[12px] font-black text-gray-400 uppercase tracking-wider">Special Discount %</label>
                <div className="relative group">
                  <Tag className="absolute left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4B9C86]" />
                  <input
                    type="number"
                    placeholder="0"
                    value={productDetails.discount}
                    onChange={(e) => handleDetailsChange('discount', e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-2xl focus:outline-none focus:border-[#4B9C86] font-black text-[#1E4D43]"
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-[12px] font-black text-gray-400 uppercase tracking-wider">Batch Number / SKU</label>
                <div className="relative group">
                  <Calculator className="absolute left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4B9C86]" />
                  <input
                    type="text"
                    placeholder="e.g. BATCH-2024-001"
                    value={productDetails.batchNumber}
                    onChange={(e) => handleDetailsChange('batchNumber', e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-2xl focus:outline-none focus:border-[#4B9C86] font-black text-[#1E4D43]"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-8 border-t border-[#E1F2ED] bg-[#F9FBFA] flex items-center justify-between">
          <p className="text-[12px] font-bold text-[#1E4D43] opacity-40 uppercase tracking-widest font-black">
            {step === 1 ? `${filteredProducts.length} Items Listed` : 'Configure Item'}
          </p>
          <div className="flex space-x-4">
            <button
              onClick={handleClose}
              className="px-6 py-3 text-[#1E4D43] font-black uppercase tracking-wider text-sm hover:underline"
            >
              Close
            </button>
            <button
              onClick={handleContinue}
              disabled={step === 1 && !selectedProduct}
              className={`px-10 py-3 rounded-2xl font-black uppercase tracking-wider text-sm transition-all shadow-lg active:scale-[0.98] ${step === 1 && !selectedProduct
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                : 'bg-[#BDDBCE] text-[#1E4D43] hover:bg-[#A9CCBD]'
                }`}
            >
              {step === 1 ? 'Select Product' : 'Add to Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SelectProductModal;